import React, { useState, useEffect, useRef } from "react";
import "./ChatEditor.css";
import IconClose from "../../common/icons/IconClose";
import { toast } from "react-toastify";
import { marked } from "marked";
import { chatVersionsApi } from "../../common/api";

const ChatEditor = ({
  isOpen,
  onClose,
  content,
  title = "Intern Presentation Guide",
  chatId,
  reviewId,
}) => {
  // If we have reviewId, we'll rely exclusively on that
  // only use fallback for chatId if absolutely needed
  const fallbackId = useRef(
    `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );

  const [currentContent, setCurrentContent] = useState(content || "");
  const [htmlContent, setHtmlContent] = useState("");
  const contentEditableRef = useRef(null);
  const originalMarkdownRef = useRef("");
  const [versions, setVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  // Always enable version support by default
  const [hasVersionSupport, setHasVersionSupport] = useState(true);

  // Update content when prop changes
  useEffect(() => {
    // Store the original markdown content
    originalMarkdownRef.current = content || "";
    setCurrentContent(content || "");

    // Convert markdown to HTML if content exists
    if (content) {
      setHtmlContent(marked.parse(content));
    } else {
      setHtmlContent("");
    }
  }, [content]);

  // Load versions when chat ID or review ID is available
  useEffect(() => {
    // Clear versions when dependencies change
    setVersions([]);
    setCurrentVersionIndex(-1);

    // Always use reviewId if available, fallback to chatId only if necessary
    if (reviewId) {
      loadVersionsByReview();
    } else if (chatId) {
      loadVersions();
    }
  }, [chatId, reviewId]);

  // Load versions from the API by chat ID
  const loadVersions = async () => {
    try {
      setIsLoading(true);
      const versionsData = await chatVersionsApi.getVersions(chatId);

      setVersions(versionsData);

      // Set current version to the latest (if any versions exist)
      if (versionsData.length > 0) {
        setCurrentVersionIndex(versionsData.length - 1);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error loading versions:", error);
      toast.error("Failed to load versions");
      setIsLoading(false);
    }
  };

  // Load versions from the API by review ID
  const loadVersionsByReview = async () => {
    try {
      setIsLoading(true);
      const versionsData = await chatVersionsApi.getVersionsByReview(reviewId);

      setVersions(versionsData);

      // Set current version to the latest (if any versions exist)
      if (versionsData.length > 0) {
        setCurrentVersionIndex(versionsData.length - 1);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error loading versions by review:", error);
      toast.error("Failed to load versions");
      setIsLoading(false);
    }
  };

  // Save current content as a new version
  const saveVersion = async () => {
    try {
      setIsLoading(true);

      // Pass reviewId as both chatId and reviewId
      const result = await chatVersionsApi.saveVersion(
        reviewId,
        currentContent,
        title,
        reviewId
      );

      // Always reload using reviewId
      await loadVersionsByReview();

      toast.success("Version saved successfully!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error saving version:", error);
      toast.error(
        `Failed to save version: ${error.message || "Unknown error"}`
      );
      setIsLoading(false);
    }
  };

  // Link existing versions to a review
  const linkVersionsToReview = async () => {
    if (!reviewId || !chatId) {
      console.error("Cannot link versions: missing review ID or chat ID");
      return;
    }

    try {
      setIsLoading(true);
      const result = await chatVersionsApi.linkVersionsToReview(
        chatId,
        reviewId
      );

      // Reload versions
      await loadVersionsByReview();

      toast.success("Chat versions linked to review successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Error linking versions to review:", error);
      toast.error(
        `Failed to link versions: ${error.message || "Unknown error"}`
      );
      setIsLoading(false);
    }
  };

  // Navigate to previous version
  const goToPreviousVersion = () => {
    if (currentVersionIndex > 0) {
      const newIndex = currentVersionIndex - 1;
      setCurrentVersionIndex(newIndex);
      const previousVersion = versions[newIndex];

      setCurrentContent(previousVersion.content || "");
      setHtmlContent(marked.parse(previousVersion.content || ""));
    } else {
      toast.info("This is the earliest version", {
        autoClose: 1500,
      });
    }
  };

  // Navigate to next version
  const goToNextVersion = () => {
    if (currentVersionIndex < versions.length - 1) {
      const newIndex = currentVersionIndex + 1;
      setCurrentVersionIndex(newIndex);
      const nextVersion = versions[newIndex];

      setCurrentContent(nextVersion.content || "");
      setHtmlContent(marked.parse(nextVersion.content || ""));
    } else {
      toast.info("This is the latest version", {
        autoClose: 1500,
      });
    }
  };

  const handleCopy = () => {
    try {
      // Copy the current markdown content
      navigator.clipboard.writeText(currentContent);
      toast.success("Content copied!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error("Error copying content", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleShare = () => {
    try {
      const mailtoLink = `mailto:?subject=${encodeURIComponent(
        title
      )}&body=${encodeURIComponent(currentContent)}`;
      window.location.href = mailtoLink;
    } catch (error) {
      toast.error("Error sharing content", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleContentChange = () => {
    if (contentEditableRef.current) {
      // Get the HTML content from the editor
      const html = contentEditableRef.current.innerHTML;

      // Convert HTML back to markdown format
      // Note: For a complete solution, you'd need a HTML-to-Markdown converter
      // This is a simplified approach that preserves basic structure
      const updatedContent = htmlToMarkdown(html);
      setCurrentContent(updatedContent);
    }
  };

  // Simple HTML to Markdown converter
  // For a production app, consider using a library like turndown.js
  const htmlToMarkdown = (html) => {
    // This is a simplified converter - it handles common formatting
    let markdown = html;

    // Replace <p> tags with newlines
    markdown = markdown.replace(/<p>(.*?)<\/p>/gi, "$1\n\n");

    // Replace <strong> or <b> with markdown bold
    markdown = markdown.replace(/<(strong|b)>(.*?)<\/(strong|b)>/gi, "**$2**");

    // Replace <em> or <i> with markdown italic
    markdown = markdown.replace(/<(em|i)>(.*?)<\/(em|i)>/gi, "*$2*");

    // Replace <h1> through <h6> with markdown headings
    markdown = markdown.replace(/<h1>(.*?)<\/h1>/gi, "# $1\n\n");
    markdown = markdown.replace(/<h2>(.*?)<\/h2>/gi, "## $1\n\n");
    markdown = markdown.replace(/<h3>(.*?)<\/h3>/gi, "### $1\n\n");
    markdown = markdown.replace(/<h4>(.*?)<\/h4>/gi, "#### $1\n\n");
    markdown = markdown.replace(/<h5>(.*?)<\/h5>/gi, "##### $1\n\n");
    markdown = markdown.replace(/<h6>(.*?)<\/h6>/gi, "###### $1\n\n");

    // Replace <ul> and <li> with markdown list
    markdown = markdown.replace(/<li>(.*?)<\/li>/gi, "- $1\n");

    // Replace <ol> and <li> with markdown numbered list
    // This is simplified and doesn't track numbers
    markdown = markdown.replace(/<ol>.*?<li>(.*?)<\/li>.*?<\/ol>/gi, "1. $1\n");

    // Replace <a> with markdown link
    markdown = markdown.replace(/<a href="(.*?)">(.*?)<\/a>/gi, "[$2]($1)");

    // Replace <code> with markdown code
    markdown = markdown.replace(/<code>(.*?)<\/code>/gi, "`$1`");

    // Replace <pre> with markdown code block
    markdown = markdown.replace(/<pre>(.*?)<\/pre>/gi, "```\n$1\n```");

    // Clean up any remaining HTML tags
    markdown = markdown.replace(/<[^>]*>/g, "");

    // Fix common HTML entities
    markdown = markdown.replace(/&nbsp;/g, " ");
    markdown = markdown.replace(/&amp;/g, "&");
    markdown = markdown.replace(/&lt;/g, "<");
    markdown = markdown.replace(/&gt;/g, ">");
    markdown = markdown.replace(/&quot;/g, '"');
    markdown = markdown.replace(/&#39;/g, "'");

    // Clean up excess newlines
    markdown = markdown.replace(/\n\n\n+/g, "\n\n");

    return markdown;
  };

  // Alternative approach: keep two editing modes
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);

  const toggleEditMode = () => {
    setIsMarkdownMode(!isMarkdownMode);
  };

  // Format version date
  const formatVersionDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get current version info
  const getCurrentVersionInfo = () => {
    if (currentVersionIndex >= 0 && versions.length > 0) {
      const version = versions[currentVersionIndex];
      return `Version ${version.version_number} - ${formatVersionDate(
        version.created_at
      )}`;
    }
    return "New version";
  };

  // Render link to review button if chat exists but isn't linked to the review yet
  const renderLinkToReviewButton = () => {
    // Only show the link button if we have a chat ID, a review ID, and at least one version
    if (chatId && reviewId && versions.length > 0) {
      // Check if any version is not linked to the review yet
      const unlinkedVersions = versions.some((v) => !v.review_id);

      if (unlinkedVersions) {
        return (
          <button
            className="action-button"
            onClick={linkVersionsToReview}
            title="Link to current review"
            disabled={isLoading}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        );
      }
    }
    return null;
  };

  return (
    <div
      className={isOpen ? "chat-editor-overlay" : "chat-editor-overlay hidden"}
    >
      <div
        className={`chat-editor ${isOpen ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-editor-header">
          <div className="chat-editor-title">
            <h3>{title}</h3>
            {versions.length > 0 && (
              <div className="version-info">{getCurrentVersionInfo()}</div>
            )}
          </div>
          <div className="chat-editor-actions">
            <button
              className="action-button"
              onClick={goToPreviousVersion}
              title="Previous version"
              disabled={currentVersionIndex <= 0 || versions.length === 0}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 19L5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="action-button"
              onClick={goToNextVersion}
              title="Next version"
              disabled={
                currentVersionIndex >= versions.length - 1 ||
                versions.length === 0
              }
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12H19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5L19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {renderLinkToReviewButton()}
            <button
              className="action-button"
              onClick={saveVersion}
              title="Save version"
              disabled={isLoading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16L21 8V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 21V13H7V21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 3V8H15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="action-button"
              onClick={handleCopy}
              title="Copy to clipboard"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0018 2.41148 15.7793 2.26012 15.5338 2.15673C15.2882 2.05334 15.0244 2.00001 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 18V20C16 20.5304 15.7893 21.0391 15.4142 21.4142C15.0391 21.7893 14.5304 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V8C4 7.46957 4.21071 6.96086 4.58579 6.58579C4.96086 6.21071 5.46957 6 6 6H8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="action-button"
              onClick={handleShare}
              title="Share via email"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 12V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 6L12 2L8 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 2V15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className="chat-editor-close"
              onClick={() => onClose(currentContent)}
            >
              <IconClose />
            </div>
          </div>
        </div>
        <div className="chat-editor-content">
          {isMarkdownMode ? (
            // Markdown text area editor
            <textarea
              className="document-content markdown-editor"
              value={currentContent}
              onChange={(e) => setCurrentContent(e.target.value)}
            />
          ) : (
            // Rich text editor
            <div className="chat-editor-document">
              <div
                ref={contentEditableRef}
                className="document-content editable"
                contentEditable="true"
                onInput={handleContentChange}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatEditor;
